import React, { useMemo } from 'react';

import { InputAdornment } from '@mui/material';

import { CurrencyTableInput } from 'src/app/components/form-elements/inputs/currency-input';
import { TableInput } from 'src/app/components/lib/input';
import { Label } from 'src/app/components/lib/label';
import { MenuItem } from 'src/app/components/lib/menu-item';
import { TableSelect } from 'src/app/components/lib/select/table-select';

import {
  rewardApplyToOptions,
  ThresholdType,
  thresholdTypes,
  TotalItemConditionType,
  totalItemsConditionTypes,
} from '../../../models';
import { getMinItemsPlaceholderFromTotalItemCondition } from '../../shared/helpers';
import { InputWithLabelWrapper, RequirementCardRow } from '../../shared/styled';
import { useDiscountWizardStateContext } from '../../state';
import { RewardsStepActions } from '../../state/rewards';

import { BetweenItemCountWrapper } from './styled';

export function ConditionRow() {
  const {
    rewardsStepDispatch,
    rewardsStepState: {
      condition: { applyTo, condition, maximumItems, minimumGrams, minimumItems, minimumSpend, totalItemCondition },
    },
  } = useDiscountWizardStateContext();

  const minimumItemsPlaceholder = useMemo(
    () => getMinItemsPlaceholderFromTotalItemCondition(totalItemCondition),
    [totalItemCondition]
  );

  return (
    <RequirementCardRow>
      <InputWithLabelWrapper>
        <Label htmlFor='select-input_condition' id='select-label_condition'>
          Condition:
        </Label>
        <TableSelect
          labelForId='condition'
          value={condition}
          width='146px'
          onChange={({ target }) =>
            rewardsStepDispatch({
              type: RewardsStepActions.SET_REWARD_CONDITION,
              payload: target.value,
            })
          }
        >
          {thresholdTypes.map((type) => (
            <MenuItem key={type.name} value={type.val}>
              {type.name}
            </MenuItem>
          ))}
        </TableSelect>
      </InputWithLabelWrapper>
      {condition === ThresholdType.NumberOfItems && (
        <>
          <InputWithLabelWrapper>
            <Label htmlFor='select-input_apply' id='select-label_apply'>
              Cart contains:
            </Label>
            <TableSelect
              labelForId='apply'
              value={totalItemCondition}
              width='146px'
              onChange={({ target }) =>
                rewardsStepDispatch({
                  type: RewardsStepActions.SET_REWARD_TOTAL_ITEM_CONDITION,
                  payload: target.value,
                })
              }
            >
              {totalItemsConditionTypes.map((type) => (
                <MenuItem key={type.name} value={type.val}>
                  {type.name}
                </MenuItem>
              ))}
            </TableSelect>
          </InputWithLabelWrapper>
          <BetweenItemCountWrapper>
            <TableInput
              placeholder={minimumItemsPlaceholder}
              type='number'
              value={minimumItems}
              width='80px'
              onChange={({ target }) =>
                rewardsStepDispatch({
                  type: RewardsStepActions.SET_REWARD_MINIMUM_ITEMS,
                  payload: target.value === '' ? '' : Number(target.value),
                })
              }
            />
            {totalItemCondition === TotalItemConditionType.Between && (
              <>
                -
                <TableInput
                  placeholder='max'
                  type='number'
                  value={maximumItems}
                  width='80px'
                  onChange={({ target }) =>
                    rewardsStepDispatch({
                      type: RewardsStepActions.SET_REWARD_MAXIMUM_ITEMS,
                      payload: target.value === '' ? '' : Number(target.value),
                    })
                  }
                />
              </>
            )}
          </BetweenItemCountWrapper>
        </>
      )}
      {condition === ThresholdType.SpendingAmount && (
        <CurrencyTableInput
          decimalPlaces={2}
          placeholder='Minimum dollar amount'
          value={minimumSpend}
          width='200px'
          onChange={(value) =>
            rewardsStepDispatch({
              type: RewardsStepActions.SET_REWARD_MINIMUM_SPEND,
              payload: value,
            })
          }
        />
      )}
      {condition === ThresholdType.ProductWeight && (
        <TableInput
          endAdornment={minimumGrams === '' ? undefined : <InputAdornment position='end'>GRAMS</InputAdornment>}
          placeholder='Minimum grams'
          type='number'
          value={minimumGrams}
          width='200px'
          onChange={({ target }) =>
            rewardsStepDispatch({
              type: RewardsStepActions.SET_REWARD_MINIMUM_GRAMS,
              payload: target.value === '' ? '' : Number(target.value),
            })
          }
        />
      )}
      {totalItemCondition === TotalItemConditionType.Exactly && (
        <InputWithLabelWrapper>
          <Label htmlFor='select-input_apply_to' id='select-label_apply_to'>
            Apply to:
          </Label>
          <TableSelect
            labelForId='apply_to'
            value={applyTo}
            width='146px'
            onChange={({ target }) =>
              rewardsStepDispatch({
                type: RewardsStepActions.SET_REWARD_APPLY_TO,
                payload: target.value,
              })
            }
          >
            {rewardApplyToOptions.map((type) => (
              <MenuItem key={type.name} value={type.val}>
                {type.name}
              </MenuItem>
            ))}
          </TableSelect>
        </InputWithLabelWrapper>
      )}
    </RequirementCardRow>
  );
}
